import React from 'react';
import { motion } from 'framer-motion';
import { Check, Gift, Calendar, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BenefitSlotProps {
  index: number;
  isUsed: boolean;
  usedDate?: string;
  redeemerName?: string;
  redeemerUbicacion?: string | null;
  isAdmin?: boolean;
  canRedeem?: boolean;
  onClick?: () => void;
}

const BenefitSlot: React.FC<BenefitSlotProps> = ({
  index,
  isUsed,
  usedDate,
  redeemerName,
  redeemerUbicacion,
  isAdmin = false,
  canRedeem = false,
  onClick,
}) => {
  const formattedDate = usedDate 
    ? new Date(usedDate).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' })
    : null;

  if (isUsed) {
    return (
      <motion.div
        initial={{ scale: 0, rotateY: 180 }}
        animate={{ scale: 1, rotateY: 0 }}
        transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}
        className="relative bg-gradient-to-br from-green-500/30 to-emerald-600/20 border-2 border-green-500/50 rounded-xl p-3 backdrop-blur-sm"
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-green-500/10 rounded-xl blur-sm" />
        
        <div className="relative flex flex-col items-center gap-1.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/30">
            <Check className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <span className="text-green-400 text-xs font-semibold">Canjeado</span>
          {formattedDate && (
            <div className="flex items-center gap-1 text-green-300/70 text-[10px]">
              <Calendar className="w-3 h-3" />
              <span>{formattedDate}</span>
            </div>
          )}
          {redeemerName && (
            <span className="text-green-300/60 text-[10px] mt-0.5">
              por {redeemerName}
            </span>
          )}
          {redeemerUbicacion && (
            <span className="flex items-center gap-0.5 text-green-300/50 text-[9px]">
              <MapPin className="w-2.5 h-2.5" />
              {redeemerUbicacion}
            </span>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      onClick={canRedeem ? onClick : undefined}
      whileHover={canRedeem ? { scale: 1.05, borderColor: 'rgba(59, 130, 246, 0.8)' } : {}}
      whileTap={canRedeem ? { scale: 0.95 } : {}}
      className={cn(
        'relative border-2 border-dashed rounded-xl p-3 backdrop-blur-sm transition-all duration-300',
        canRedeem 
          ? 'border-blue-500/50 bg-blue-500/10 cursor-pointer hover:bg-blue-500/20' 
          : 'border-white/20 bg-white/5'
      )}
    >
      {/* Pulse animation for available slots */}
      {canRedeem && (
        <motion.div
          animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl"
        />
      )}
      
      <div className="relative flex flex-col items-center gap-1.5">
        <motion.div 
          animate={canRedeem ? { y: [0, -3, 0] } : {}}
          transition={{ duration: 1.5, repeat: Infinity }}
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center',
            canRedeem 
              ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/30' 
              : 'bg-white/10'
          )}
        >
          <Gift className={cn(
            'w-5 h-5',
            canRedeem ? 'text-blue-400' : 'text-white/40'
          )} />
        </motion.div>
        <span className={cn(
          'text-xs font-medium',
          canRedeem ? 'text-blue-400' : 'text-white/40'
        )}>
          {canRedeem ? 'Disponible' : 'Pendiente'}
        </span>
        {isAdmin && canRedeem && (
          <span className="text-blue-500/70 text-[10px]">Click para canjear</span>
        )}
      </div>
    </motion.div>
  );
};

export default BenefitSlot;
